import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { ArrowRightIcon, CheckCircle2Icon, StarIcon, ShieldCheckIcon, ZapIcon, TrophyIcon } from 'lucide-react';
import { SERVICES } from '../utils/servicesData';
const HUB_STATS = [{
  value: '1,000+',
  label: 'Stores delivered'
}, {
  value: '700+',
  label: '5-star reviews'
}, {
  value: '100M+',
  label: 'Client revenue'
}, {
  value: '100%',
  label: 'Shopify focused'
}];
const VALUE_PROPS = [{
  icon: <ShieldCheckIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1563" data-mp="2l1fu5a8pihuu" />,
  title: 'Shopify specialists',
  desc: 'We do one thing exceptionally well: Shopify. No generalists, no guesswork.'
}, {
  icon: <ZapIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1564" data-mp="2uzdj5zye4ioi" />,
  title: 'Conversion-obsessed',
  desc: 'Every decision is made to grow your revenue, not just look good.'
}, {
  icon: <TrophyIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1565" data-mp="2xjria8wn4k5m" />,
  title: 'Fiverr Pro verified',
  desc: 'Vetted by Fiverr’s Pro review process and featured by leading ecommerce platforms.'
}];
export function ServicesPage() {
  usePageSEO({
    canonical: '/services',
    keywords: 'Shopify services, Shopify store development, Shopify theme development, Shopify app development, conversion rate optimization',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Services',
      path: '/services'
    }]),
    title: 'Shopify Services | Design Flow Digitals',
    description: 'Premium Shopify services: store development, custom theme development, app development, and conversion rate optimization. Built by Shopify specialists to grow your revenue.'
  });
  return <div className="w-full" data-id="element-1566" data-mp="2x77nkr1tc2c">
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden" data-id="element-1567" data-mp="3uajugvggds24">
        <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none" data-id="element-1568" data-mp="3u87722rxepho" />
        <div className="absolute top-0 right-1/4 w-[36rem] h-[36rem] bg-[#D4AF37]/10 rounded-full blur-[140px] animate-pulse-glow pointer-events-none" data-id="element-1569" data-mp="1sdjeycottrmd" />

        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10 flex flex-col items-center" data-id="element-1570" data-mp="1inq79mrt1wne">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-1571" data-mp="1nkzuqwvmvy8l">
            <StarIcon className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" data-id="element-1572" data-mp="19kp2pqil8dlu" />
            <span className="text-sm font-semibold text-[#F3E5AB] tracking-wide uppercase" data-id="element-1573" data-mp="1emedd51wep9e">
              Premium Shopify Services
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight" data-id="element-1574" data-mp="14n734qqgnaqi">
            Services that grow your Shopify revenue
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mb-10" data-id="element-1575" data-mp="2u3ev0gcvuzp9">
            End-to-end Shopify solutions, from store builds to custom apps and
            conversion optimization, engineered by specialists for ambitious
            brands.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-5 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1576" data-mp="ifjfzrj02364">
            Start Your Project <ArrowRightIcon className="w-5 h-5" data-id="element-1577" data-mp="1642n43qbc9wm" />
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-1578" data-mp="123advahfy50c">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1579" data-mp="1253kips20p8c">
          {HUB_STATS.map(s => <div key={s.label} className="text-center" data-id="element-1580" data-mp="1b35sjpa7gi54">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-1581" data-mp="1b1mc9yoju73c">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1582" data-mp="3cv22iugq5jxs">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Service Cards */}
      <section className="section-padding bg-black" data-id="element-1583" data-mp="3ebfvbp8ruv2u">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1584" data-mp="3jasm2l2rkmap">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1585" data-mp="3jd5anh311uwq">
            What we do best
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1586" data-mp="3o8au898yixhn">
            Choose a service to see exactly how we deliver results.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-1587" data-mp="2vm9bfyw8rxfg">
          {SERVICES.map(service => {
          const Icon = service.icon;
          return <Link key={service.slug} to={`/services/${service.slug}`} className="group glass-card p-10 flex flex-col h-full border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden" data-id="element-1588" data-mp="35j480c8yfnr3">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1589" data-mp="2ht9vxwwgm37w" />

                <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1590" data-mp="ywukyx5vqg35">
                  <Icon className="w-8 h-8 text-[#D4AF37]" data-id="element-1591" data-mp="pwfqb770zomz" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors" data-id="element-1592" data-mp="kynvo5ezxof3">
                  {service.title}
                </h3>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1593" data-mp="b94bmwfjroin">
                  {service.heroSubhead}
                </p>

                <ul className="space-y-3 mb-8" data-id="element-1594" data-mp="78ar8d1bcrui">
                  {service.deliverables.slice(0, 3).map(d => <li key={d.title} className="flex items-center gap-3 text-gray-300" data-id="element-1595" data-mp="26ldgpmpi02q">
                      <CheckCircle2Icon className="w-5 h-5 text-[#D4AF37] shrink-0" data-id="element-1596" data-mp="3ogxmmo76j0ow" />
                      {d.title}
                    </li>)}
                </ul>

                <span className="mt-auto inline-flex items-center gap-2 text-[#D4AF37] font-semibold" data-id="element-1597" data-mp="3jrwnpsx7sp8l">
                  Explore {service.title}{' '}
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1598" data-mp="35tf529m622xj" />
                </span>
              </Link>;
        })}
        </div>
      </section>

      {/* Why Us */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1599" data-mp="1fqfglqwf9zf">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1600" data-mp="6el4w6jdfwsi">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1601" data-mp="3opeyzpypfgjr">
            Why brands trust Design Flow Digitals
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-1602" data-mp="3sq8mwjc5cvcn">
          {VALUE_PROPS.map(v => <div key={v.title} className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-colors" data-id="element-1603" data-mp="kaf2vuilx1qu">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-6" data-id="element-1604" data-mp="29s2huxcyb979">
                {v.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1605" data-mp="2jjghr68teb0x">{v.title}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1606" data-mp="2ekdgzxzvaqyr">{v.desc}</p>
            </div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-[#D4AF37]/5" data-id="element-1607" data-mp="1qv1bb83r1117">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1608" data-mp="1lwzm05fqc5ha">
          Let’s build something exceptional
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1609" data-mp="20qm9ev5p5h5o">
          Tell us about your store and goals, and we’ll show you exactly how
          we’ll help you grow.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-5 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1610" data-mp="3c617y3dk0h2w">
          Book a free consultation <ArrowRightIcon className="w-5 h-5" data-id="element-1611" data-mp="19lyt2lmhj1bl" />
        </Link>
      </section>
    </div>;
}